import ExpandMoreIcon from '@offline-mui/icons-material/ExpandMore'
import RestartAltIcon from '@offline-mui/icons-material/RestartAlt'
import SearchIcon from '@offline-mui/icons-material/Search'
import TuneOutlinedIcon from '@offline-mui/icons-material/TuneOutlined'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@offline-mui/material'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { DateField } from './ui/DateField'
import SectionCard from './ui/SectionCard'

export type FlightSearchValues = {
  // Primary
  departureAirport: string
  arrivalAirport: string
  departureDateFrom: string
  departureDateTo: string
  carriers: string[]
  cabins: string[]
  // Secondary
  fareType: string
  platingCarriers: string[]
  flightNumber: string
  classesOfService: string[]
  brandCodes: string[]
  fareBasisCode: string
  lastTicketableFrom: string
  lastTicketableTo: string
}

export const EMPTY_SEARCH: FlightSearchValues = {
  departureAirport: '',
  arrivalAirport: '',
  departureDateFrom: '',
  departureDateTo: '',
  carriers: [],
  cabins: [],
  fareType: '',
  platingCarriers: [],
  flightNumber: '',
  classesOfService: [],
  brandCodes: [],
  fareBasisCode: '',
  lastTicketableFrom: '',
  lastTicketableTo: '',
}

/** "AC, ba ua" → ["AC", "BA", "UA"] */
function parseCodes(text: string): string[] {
  return Array.from(
    new Set(
      text
        .split(/[\s,]+/)
        .map((code) => code.trim().toUpperCase())
        .filter(Boolean),
    ),
  )
}
const CABINS = ['Economy', 'Premium Economy', 'Business']
const FARE_TYPES = ['Published', 'Negotiated']
const BRANDS = [
  { code: 'BASIC', name: 'BASIC ECONOMY' },
  { code: 'MAIN', name: 'MAIN CABIN' },
  { code: 'FLEX', name: 'FLEX ECONOMY' },
  { code: 'PREM', name: 'PREMIUM ECONOMY' },
  { code: 'BIZ', name: 'BUSINESS SAVER' },
]

const SECONDARY_KEYS: (keyof FlightSearchValues)[] = [
  'fareType',
  'platingCarriers',
  'flightNumber',
  'classesOfService',
  'brandCodes',
  'fareBasisCode',
  'lastTicketableFrom',
  'lastTicketableTo',
]

function isSet(value: string | string[]) {
  return Array.isArray(value) ? value.length > 0 : value !== ''
}

function countActive(values: FlightSearchValues, keys?: (keyof FlightSearchValues)[]) {
  const source = keys ?? (Object.keys(values) as (keyof FlightSearchValues)[])
  return source.filter((key) => isSet(values[key])).length
}

function GroupLabel({ children }: { children: string }) {
  return (
    <Typography
      variant="overline"
      sx={{ color: 'text.secondary', display: 'block', mb: 1.25 }}
    >
      {children}
    </Typography>
  )
}

type FlightSearchFormProps = {
  onSearch?: (values: FlightSearchValues) => void
  onReset?: () => void
}

export default function FlightSearchForm({ onSearch, onReset }: FlightSearchFormProps) {
  const [values, setValues] = useState<FlightSearchValues>(EMPTY_SEARCH)
  const [moreOpen, setMoreOpen] = useState(false)
  // Raw text for the comma-separated code lists, kept separately so the user
  // can type commas and spaces freely; the parsed arrays live in `values`.
  const [carriersText, setCarriersText] = useState('')
  const [platingText, setPlatingText] = useState('')
  const [classesText, setClassesText] = useState('')

  const update = <K extends keyof FlightSearchValues>(
    key: K,
    value: FlightSearchValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    onSearch?.(values)
  }

  const handleReset = () => {
    setValues(EMPTY_SEARCH)
    setCarriersText('')
    setPlatingText('')
    setClassesText('')
    onReset?.()
  }

  const activeTotal = countActive(values)
  const activeSecondary = countActive(values, SECONDARY_KEYS)

  return (
    <SectionCard
      component="form"
      onSubmit={handleSubmit}
      title="Search filters"
      subtitle="Leave everything blank to load the full inventory."
      icon={<TuneOutlinedIcon />}
      action={
        activeTotal > 0 ? (
          <Chip
            size="small"
            color="primary"
            label={`${activeTotal} active filter${activeTotal === 1 ? '' : 's'}`}
          />
        ) : null
      }
    >
      <Stack spacing={3}>
        <Box>
          <GroupLabel>Route & travel dates</GroupLabel>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="From"
                placeholder="YYZ"
                value={values.departureAirport}
                onChange={(e) => update('departureAirport', e.target.value.toUpperCase())}
                slotProps={{ htmlInput: { maxLength: 3, style: { textTransform: 'uppercase' } } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="To"
                placeholder="CDG"
                value={values.arrivalAirport}
                onChange={(e) => update('arrivalAirport', e.target.value.toUpperCase())}
                slotProps={{ htmlInput: { maxLength: 3, style: { textTransform: 'uppercase' } } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <DateField
                label="Departure from"
                value={values.departureDateFrom}
                onChange={(value) => update('departureDateFrom', value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <DateField
                label="Departure to"
                value={values.departureDateTo}
                onChange={(value) => update('departureDateTo', value)}
                minDate={values.departureDateFrom || undefined}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <TextField
                fullWidth
                label="Carrier"
                placeholder="AC, AA"
                value={carriersText}
                onChange={(e) => {
                  setCarriersText(e.target.value.toUpperCase())
                  update('carriers', parseCodes(e.target.value))
                }}
                helperText="Airline codes, comma-separated"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Autocomplete
                multiple
                options={CABINS}
                value={values.cabins}
                onChange={(_, value) => update('cabins', value)}
                renderInput={(params) => (
                  <TextField {...params} label="Cabin" placeholder="Select cabins" />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Button
            type="button"
            size="small"
            onClick={() => setMoreOpen((open) => !open)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: moreOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform .2s ease',
                }}
              />
            }
            sx={{ px: 1, ml: -1, color: 'text.primary' }}
          >
            More filters
            {activeSecondary > 0 ? (
              <Chip
                size="small"
                color="primary"
                label={activeSecondary}
                sx={{ ml: 1, height: 20, '& .MuiChip-label': { px: 0.75 } }}
              />
            ) : null}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Fare type, plating carrier, flight number, class, brand and ticketability.
          </Typography>

          <Collapse in={moreOpen} unmountOnExit={false}>
            <Grid container spacing={2} sx={{ mt: 1.5 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  select
                  fullWidth
                  label="Fare type"
                  value={values.fareType}
                  onChange={(e) => update('fareType', e.target.value)}
                >
                  <MenuItem value="">Any</MenuItem>
                  {FARE_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label="Plating carrier"
                  placeholder="AC, AA"
                  value={platingText}
                  onChange={(e) => {
                    setPlatingText(e.target.value.toUpperCase())
                    update('platingCarriers', parseCodes(e.target.value))
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label="Flight number"
                  placeholder="e.g. 100"
                  value={values.flightNumber}
                  onChange={(e) => update('flightNumber', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label="Fare basis code"
                  placeholder="e.g. YL2N0C9S"
                  value={values.fareBasisCode}
                  onChange={(e) => update('fareBasisCode', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  label="Class of service"
                  placeholder="Y, B, M"
                  value={classesText}
                  onChange={(e) => {
                    setClassesText(e.target.value.toUpperCase())
                    update('classesOfService', parseCodes(e.target.value))
                  }}
                  helperText="Booking classes, comma-separated"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Autocomplete
                  multiple
                  options={BRANDS.map((b) => b.code)}
                  getOptionLabel={(code) => {
                    const brand = BRANDS.find((b) => b.code === code)
                    return brand ? `${brand.code} — ${brand.name}` : code
                  }}
                  value={values.brandCodes}
                  onChange={(_, value) => update('brandCodes', value)}
                  renderInput={(params) => (
                    <TextField {...params} label="Brand" placeholder="Select brands" />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <DateField
                  label="Ticketable from"
                  value={values.lastTicketableFrom}
                  onChange={(value) => update('lastTicketableFrom', value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <DateField
                  label="Ticketable to"
                  value={values.lastTicketableTo}
                  onChange={(value) => update('lastTicketableTo', value)}
                  minDate={values.lastTicketableFrom || undefined}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Box>

        <Divider />

        <Stack
          direction="row"
          spacing={1.5}
          sx={{ justifyContent: 'flex-end', alignItems: 'center' }}
        >
          <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset}>
            Reset
          </Button>
          <Button type="submit" variant="contained" startIcon={<SearchIcon />}>
            Search inventory
          </Button>
        </Stack>
      </Stack>
    </SectionCard>
  )
}
